import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import CartPage from '../pages/CartPage';
import ComparePage from '../pages/ComparePage';
import CustomerAccount from './CustomerAccount';
import { useCustomer } from '../sections/SiteChrome';

/* Unified glass modal shell: cart / compare / login share the same theme.
   Desktop: centered dialog (max 90vh). Mobile: bottom sheet, full width,
   at least 20px gap from top, no outer margins. */
function Shell({ title, onClose, children, wide }) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [onClose]);
  return (
    <div onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(12,10,9,0.55)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
        padding: isMobile ? 0 : 16, direction: 'rtl',
      }}>
      <div onClick={e => e.stopPropagation()}
        className={isMobile ? 'quick-sheet' : undefined}
        style={{
          background: 'var(--card)', color: 'var(--card-fg, var(--fg))',
          border: '1px solid var(--border)',
          borderRadius: isMobile ? '20px 20px 0 0' : 20,
          borderBottom: isMobile ? 'none' : undefined,
          width: isMobile ? '100%' : (wide ? 'min(860px, 96%)' : 'min(560px, 96%)'),
          maxHeight: isMobile ? 'min(90vh, calc(100dvh - 20px))' : '90vh',
          overflowY: 'auto', padding: 22,
          boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
        }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontWeight: 800, fontSize: '1.05rem' }}>{title}</h2>
          <button onClick={onClose} aria-label="بستن"
            style={{ width: 34, height: 34, borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--fg)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function QuickModals() {
  const [which, setWhich] = useState(null);
  const [flow, setFlow] = useState(null); // 'order' = continue checkout without reload
  const { setCustomer } = useCustomer();
  useEffect(() => {
    const open = name => () => { setFlow(null); setWhich(name); };
    const oc = open('cart'), om = open('compare');
    const ol = e => { setFlow(e?.detail?.flow || null); setWhich('login'); };
    window.addEventListener('open-cart', oc);
    window.addEventListener('open-compare', om);
    window.addEventListener('open-login', ol);
    return () => {
      window.removeEventListener('open-cart', oc);
      window.removeEventListener('open-compare', om);
      window.removeEventListener('open-login', ol);
    };
  }, []);
  if (!which) return null;
  const close = () => { setWhich(null); setFlow(null); };
  if (which === 'cart') return <Shell title="سبد خرید" onClose={close} wide><CartPage embedded onDone={close} /></Shell>;
  if (which === 'compare') return <Shell title="مقایسه" onClose={close} wide><ComparePage embedded onDone={close} /></Shell>;
  return (
    <Shell title="ورود | ثبت‌نام" onClose={close}>
      <CustomerAccount compact onDone={({ user }) => {
        if (!user) return;
        setCustomer(user);
        window.dispatchEvent(new Event('customer-changed'));
        if (flow === 'order') {
          close();
          window.dispatchEvent(new Event('cart-updated'));
          window.dispatchEvent(new Event('compare-updated'));
        } else {
          window.location.reload();
        }
      }} />
    </Shell>
  );
}
